package modysy.tools

import modysy.GameController
import modysy.model._
import modysy.simulation.pp.State
import modysy.view.{Color, ImageProvider}
import org.scalajs.dom

/** Implements a tool for placing agents with the given State into the game world
  * controlled by the given GameController.
  *
  * @param gameController the GameController controlling the game world
  * @param state          the state any agents placed will have initially
  * @see Agent
  */
class AgentPlacementTool(
    override protected val gameController: GameController,
    state: State
) extends PlacementTool {

  override protected var currentOrientation = 0

  /** A dummy object used to check for collisions with objects already
    * present in the level. */
  override protected val placedObject = new Agent(
    new RigidBody(Transform.identity, Velocity.zero, Material(1, 1), Circle(1)),
    state
  )

  /** Renders the icon of this tool onto the given rendering context.
    * The icon is drawn with its center at (0, 0) so the context
    * need to be translated correctly.
    *
    * @param ctx context to render the icon on
    */
  override def renderIcon(ctx: dom.CanvasRenderingContext2D): Unit = {
    ctx.save()
    ctx.scale(0.4, 0.4)
    ctx.fillStyle = state.deducedAgentColor
    ctx.beginPath()
    ctx.arc(0, 0, 50, 0, 2 * Math.PI)
    ctx.closePath()
    ctx.fill()
    ImageProvider
      .drawImgFnc(ImageProvider.getImage("agent"), 110, 172)
      .apply(ctx, -(110 / 2), -(172 / 2))
    ctx.restore()
  }

  /** Renders the non-icon parts of this tool at the tools position set
    * by moveTo(...) or moveBy(...).
    * The tool must be positioned somewhere in the game world before.
    *
    * @param ctx context to render on
    */
  override def render(ctx: dom.CanvasRenderingContext2D): Unit = {
    require(
      _position.isDefined,
      "rendering but AgentPlacementTool not positioned"
    )

    ctx.save()
    ctx.globalAlpha = 0.5

    if (isPlaceable(_position.get)) {
      ctx.fillStyle = state.deducedAgentColor
    } else {
      ctx.fillStyle = Color.Red_LightLight
    }

    ctx.beginPath()
    ctx.arc(
      _position.get.x,
      _position.get.y,
      state.deducedAgentRadius,
      0,
      2 * Math.PI
    )
    ctx.closePath()
    ctx.fill()
    ctx.restore()
    ImageProvider
      .drawImgFnc(ImageProvider.getImage("agent"), 110, 172)
      .apply(ctx, _position.get.x - (110 / 2), _position.get.y - (172 / 2))
  }

  /** Uses this tool at the tools position set by moveTo(...) or moveBy(...).
    * The tool must be positioned somewhere in the game world before.
    *
    * Returns true if the tool was used successfully, otherwise false.
    *
    * @return Whether the interaction was successful.
    */
  override def use(): Boolean = {
    require(
      _position.isDefined,
      "called use() but AgentPlacementTool not positioned"
    )

    if (isPlaceable(_position.get)) {
      val gameObject: Agent =
        new Agent(
          new RigidBody(
            Transform.translate((_position.get.x, _position.get.y)),
            Velocity.zero,
            Material(1, 1),
            Circle(state.deducedAgentRadius)
          ),
          state
        )
      gameController.addGameObject(gameObject)

      true
    } else false
  }

  /** Returns the non-negative amount of energy points needed to
    * use this tool once. This amount of points will be spent
    * using this tool once.
    *
    * @return energy cost to use this tool once
    */
  override def cost: Int = 0

  override var uses: Int = 999
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/2d4fb5cd/0/mtv-pr-2018/modysy-product/src/main/scala/modysy/tools/AgentPlacementTool.scala